import { Box } from '../UI-CHAKRA/Box';
import { Flex } from '../UI-CHAKRA/Flex';
import { Text } from '../UI-CHAKRA/Text';

import { Button } from '../UI-CHAKRA/Button';

import { CERTIFICATES } from '../../containers/certificate/certificates';
import { useCallback, useContext, useEffect, useState } from 'react';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { Tracker } from '../../shared/tracker/Tracker';
import { useLoading } from '../../helpers/useLoading';
import { onlyDigits } from '../../helpers/string';
import { CertificateFromLacuna, LacunaService } from '../../containers/certificate/lacunaService';
import { DialogWrappedComponentProps, FeedbackContext } from '../../providers/FeedbackProvider';
import { DialogRedirectToInstallWebPKI } from '../../containers/prescription/PrescriptionEmission/DialogRedirectToInstallWebPKI';
import { safeGetFromLocalStorage, safeSetInLocalStorage } from '../../helpers/window';
import { CircularProgress } from '@chakra-ui/react';
import { FormControl } from '../UI-CHAKRA/FormControl';
import { FormLabel } from '../UI-CHAKRA/FormLabel';
import { RadioGroup } from '../UI-CHAKRA/RadioGroup';
import { Radio } from '../UI-CHAKRA/Radio';
import { DateFromJSDateToBrazilianFormat } from '../../helpers/Date';
import { ReactComponent as CertificateNotFound } from '../../assets/images/certificate_not_found.svg';
import { PrescriptionContext } from '../../providers/PrescriptionProvider';

const NoCertificatesFound = () => {
  return (
    <Flex
      mt={2}
      justifyContent={'center'}
      alignItems={'center'}
      style={{ borderRadius: '8px', backgroundColor: '#F1F3F5', padding: '20px', gap: '12px' }}
      data-testid="local-certificate-not-found"
    >
      <Box>
        <CertificateNotFound />
      </Box>
      <Box>
        <Text fontSize={'15px'} fontWeight={500} color={'#868E96'}>
          Não encontramos nenhum certificado válido com seu CPF
        </Text>
      </Box>
    </Flex>
  );
};

interface LocalCertificateFlowProps {
  onClose(): void;
}

export default function LocalCertificateFlow(props: LocalCertificateFlowProps) {
  const { prescription, setSelectedLocalCertificate, selectedLocalCertificate, setSelectedCertificateType } =
    usePrescriptionStore();

  const [loadingCertificates, runWithLoadingCertificates] = useLoading();

  const { openBaseDialog } = useContext(FeedbackContext);

  const [localCertificates, setLocalCertificates] = useState<CertificateFromLacuna[]>([]);

  const [tempSelectedLocalCertificate, setTempSelectedLocalCertificate] = useState<CertificateFromLacuna | null>(
    selectedLocalCertificate
  );

  const { localCertificateValidation } = useContext(PrescriptionContext);

  const loadLocalCertificates = useCallback(() => {
    runWithLoadingCertificates(async () => {
      if (!prescription) return;

      const prescriberDocument = onlyDigits(prescription.Prescritor.Documento);

      let certificates: CertificateFromLacuna[] = [];

      try {
        certificates = LacunaService.filterExpiredCertificates(await LacunaService.listCertificates());
      } catch (error) {
        return openBaseDialog(
          (dialogProps: DialogWrappedComponentProps) => (
            <DialogRedirectToInstallWebPKI
              onGoBack={() => {
                dialogProps.onClose();
              }}
            />
          ),
          {
            width: '280px',
            height: '370px',
            persist: false,
          }
        );
      }

      if (!certificates) return;

      const userLocalCertificates = certificates.filter((certificate) => {
        return onlyDigits(certificate.pkiBrazil.cpf || '') === prescriberDocument;
      });

      setLocalCertificates(userLocalCertificates);
      const certificateFromLocalStorage = safeGetFromLocalStorage('certificadoLocalSelecionado');

      if (!!certificateFromLocalStorage) {
        const lastUsedCertificate: CertificateFromLacuna = JSON.parse(certificateFromLocalStorage);
        const certificate = certificates.find((c) => c.thumbprint === lastUsedCertificate.thumbprint);
        if (!certificate) return;
        setSelectedLocalCertificate(certificate);
        return;
      }

      if (userLocalCertificates.length === 1) {
        setTempSelectedLocalCertificate(userLocalCertificates[0]);
        setSelectedLocalCertificate(userLocalCertificates[0]);
      }
    });
  }, [openBaseDialog, prescription, runWithLoadingCertificates, setSelectedLocalCertificate]);

  const onSelectLocalCertificate = (value: string) => {
    const certificate = localCertificates.find((localCertificate) => localCertificate.thumbprint === value);

    if (!certificate) return;

    setTempSelectedLocalCertificate(certificate);
  };

  const onConfirmLocalCertificate = () => {
    if (!tempSelectedLocalCertificate) return;

    safeSetInLocalStorage('certificadoLocalSelecionado', JSON.stringify(tempSelectedLocalCertificate));
    setSelectedLocalCertificate(tempSelectedLocalCertificate);

    Tracker.trackEvent({
      event: 'A1/A3 Certificate Selected',
      parameters: {
        'Certificate name': tempSelectedLocalCertificate.subjectName,
      },
    });

    setSelectedCertificateType(CERTIFICATES.LOCAL);

    props.onClose();
  };

  useEffect(() => {
    loadLocalCertificates();
  }, [loadLocalCertificates]);

  if (loadingCertificates) {
    return (
      <Flex justifyContent="center" data-testid="local-certificates-loading">
        <CircularProgress isIndeterminate color="primary.main" />
      </Flex>
    );
  }

  if (localCertificates.length > 0 && !loadingCertificates) {
    return (
      <Flex flexDirection="column" gap="20px" data-testid="local-certificate-found">
        <FormControl isInvalid={localCertificateValidation.hasError}>
          <FormLabel mb={'20px'}>
            <Text fontSize="18px" fontWeight={700} color="#868E96">
              Selecione o certificado
            </Text>
          </FormLabel>
          <RadioGroup onChange={onSelectLocalCertificate} value={tempSelectedLocalCertificate?.thumbprint}>
            <Flex gap={'20px'}>
              {localCertificates.map((localCertificate) => (
                <Flex
                  p={'10px'}
                  borderRadius={'20px'}
                  border={
                    tempSelectedLocalCertificate?.thumbprint === localCertificate.thumbprint ? '2px solid #3484FA' : ''
                  }
                >
                  <Radio
                    data-testid="local-certificate"
                    value={localCertificate.thumbprint}
                    key={localCertificate.thumbprint}
                  >
                    <Text fontSize="14px" color="gray.900" fontFamily="Inter" ml="10px">
                      {localCertificate.subjectName}
                      <br />
                      Certificadora: {localCertificate.issuerName}
                      <br />
                      CPF: {localCertificate.pkiBrazil.cpf}
                      <br />
                      Valido até: {DateFromJSDateToBrazilianFormat(localCertificate.validityEnd)}
                    </Text>
                  </Radio>
                </Flex>
              ))}
            </Flex>
          </RadioGroup>
        </FormControl>
        <Flex justifyContent="center">
          <Button
            data-testid="local-certificate-confirm-button"
            variant="solid"
            maxWidth="90%"
            width="360px"
            p="20px"
            fontSize="14px"
            fontWeight={700}
            borderRadius="40px"
            height="57px"
            onClick={onConfirmLocalCertificate}
          >
            Ativar certificado
          </Button>
        </Flex>
      </Flex>
    );
  }

  return <NoCertificatesFound />;
}
